import { GroupId, Message } from '@sr/types';
import { createSelectorHooks } from 'auto-zustand-selectors-hook';
import { enableMapSet } from 'immer';
import create from 'zustand';
import immerMiddleware from './immer-middleware';
import {
  getMessagesForActiveGroup,
  getMessagesForGroup,
} from './selectors/messages';
import trackEvent from 'utils/analytics';

enableMapSet();

export interface Store {
  messages: Map<string, Message>;
  messagesLoaded: boolean;
  activeMessageId: string | null;
  initializeMessages: (messages: Message[]) => void;
  setMessage: (message: Message) => void;
  deleteMessage: (message: Message) => void;
  setActiveMessageId: (id: string) => void;
  clearActiveMessageId: () => void;

  elements: Map<string, HTMLElement>;
  skipNextElementFocus: boolean;
  setElement: (srId: string, elementInfo: HTMLElement) => void;
  setSkipNextElementFocus: (skip: boolean) => void;

  activeSrId: string | null;
  setActiveSrId: (id: string) => void;

  isPro: boolean;
  setIsPro: (isPro: boolean) => void;

  activeGroupId: GroupId | null;
  activeGroupMessageIndex: number | null;
  selectGroup: (grouId: GroupId) => void;
  deselectGroup: () => void;

  nextMessage: () => void;
  previousMessage: () => void;
  handleFieldFocused: (srId: string) => void;
  handleUnderlineClick: (messageId: string) => void;
  handleMessageClicked: (
    messageId: string,
    srId: string
  ) => { wasMadeActive: boolean };
}

const useStoreBase = create<Store>(
  immerMiddleware((set, get) => ({
    messages: new Map(),
    messagesLoaded: false,
    activeMessageId: null,
    // Initialize the store with new messages for a new resume
    initializeMessages: (messages) => {
      set((draft) => {
        draft.messages.clear();
        messages.forEach((message) => {
          draft.messages.set(message.id, message);
        });
        draft.messagesLoaded = true;
        draft.activeMessageId = null;
        draft.activeGroupId = null;
        draft.activeGroupMessageIndex = null;
        draft.skipNextElementFocus = false;
      });
    },
    setMessage: (message) => {
      set((draft) => {
        const previousMessage = draft.messages.get(message.id);
        draft.messages.set(message.id, message);

        // Handle when the activeMessageId is being changed from an error to success state
        // and will no longer be shown in the group.
        if (
          previousMessage !== undefined &&
          previousMessage.status !== 'ok' &&
          message.status === 'ok'
        ) {
          handleActiveMessageIdChange(draft, message.id);
        }
      });
    },
    deleteMessage: (message) => {
      set((draft) => {
        draft.messages.delete(message.id);

        // Handle the case where we are deleting the activeMessageId
        handleActiveMessageIdChange(draft, message.id);
      });
    },
    setActiveMessageId: (messageId) => {
      set((draft) => {
        if (draft.activeMessageId !== messageId) {
          draft.activeMessageId = messageId;
        }
      });
    },
    clearActiveMessageId: () => {
      set((draft) => {
        draft.activeMessageId = null;
      });
    },

    elements: new Map(),
    skipNextElementFocus: false,
    setElement: (srId, element) => {
      set((draft) => {
        draft.elements.set(srId, element);
      });
    },
    setSkipNextElementFocus: (skip: boolean) => {
      set((draft) => {
        draft.skipNextElementFocus = skip;
      });
    },

    activeSrId: null,
    setActiveSrId: (srId) => {
      set((draft) => {
        if (draft.activeSrId !== srId) {
          draft.activeSrId = srId;
        }
      });
    },

    isPro: false,
    setIsPro: (isPro: boolean) => {
      set((draft) => {
        draft.isPro = isPro;
      });
    },

    activeGroupId: null,
    activeGroupMessageIndex: null,
    selectGroup: (groupId: GroupId) => {
      set((draft) => {
        const messages = getMessagesForGroup(groupId)(draft);
        const firstMessageId = messages[0]?.id;
        if (firstMessageId) {
          draft.activeMessageId = firstMessageId;
          draft.activeGroupMessageIndex = 0;
        } else {
          draft.activeMessageId = null;
        }
        draft.activeGroupId = groupId;
        trackEvent.reviewGroupSelected({ groupId: draft.activeGroupId });
      });
    },
    deselectGroup: () => {
      set((draft) => {
        draft.activeMessageId = null;
        draft.activeGroupId = null;
        draft.activeGroupMessageIndex = null;
      });
    },

    nextMessage: () => {
      set((draft) => {
        if (!draft.activeGroupId)
          throw new Error('state.activeGroupId cannot be null');
        const messages = getMessagesForActiveGroup(draft);
        const index = messages.findIndex(
          (message) => message.id === draft.activeMessageId
        );
        if (index === messages.length - 1) {
          const nextIndex = 0;
          draft.activeMessageId = messages[nextIndex].id;
          draft.activeGroupMessageIndex = nextIndex;
        } else {
          const nextIndex = index + 1;
          draft.activeMessageId = messages[nextIndex].id;
          draft.activeGroupMessageIndex = nextIndex;
        }
        trackEvent.nextReviewMessage({ groupId: draft.activeGroupId });
      });
    },
    previousMessage: () => {
      set((draft) => {
        if (!draft.activeGroupId)
          throw new Error('state.activeGroupId cannot be null');
        const messages = getMessagesForActiveGroup(draft);
        const index = messages.findIndex(
          (message) => message.id === draft.activeMessageId
        );
        if (index === 0) {
          const nextIndex = messages.length - 1;
          draft.activeMessageId = messages[nextIndex].id;
          draft.activeGroupMessageIndex = nextIndex;
        } else {
          const nextIndex = index - 1;
          draft.activeMessageId = messages[nextIndex].id;
          draft.activeGroupMessageIndex = nextIndex;
        }
        trackEvent.previousReviewMessage({ groupId: draft.activeGroupId });
      });
    },
    handleFieldFocused: (srId: string) => {
      set((draft) => {
        draft.activeSrId = srId;
      });
    },
    handleUnderlineClick: (messageId: string) => {
      set((draft) => {
        // Deselect the message if it's already active
        if (messageId === draft.activeMessageId) {
          draft.activeMessageId = null;
          draft.activeGroupId = null;
        }
      });
    },
    handleMessageClicked: (messageId: string, srId: string) => {
      // Let the caller know if the message was focused
      let wasMadeActive = false;
      set((draft) => {
        // Make the message active if it isn't currently active
        if (messageId !== draft.activeMessageId) {
          draft.activeMessageId = messageId;
          draft.activeSrId = srId;
          wasMadeActive = true;
        } else {
          // Otherwise make the message inactive
          draft.activeMessageId = null;
          draft.activeSrId = null;
        }
      });
      return { wasMadeActive };
    },
  }))
);

// Handles when the activeMessageId is being removed or changed from error to ok
function handleActiveMessageIdChange(draft: Store, messageId: string) {
  // Handle the case where we are deleting the activeMessageId
  if (draft.activeMessageId === messageId) {
    const messages = getMessagesForActiveGroup(draft);
    // Clear the activeMessageId if there is no active group
    if (
      draft.activeGroupId === null ||
      draft.activeGroupMessageIndex === null
    ) {
      draft.activeMessageId = null;
    } else if (messages.length === 0) {
      // Handle the case where the last message in the group was removed
      draft.activeMessageId = null;
      draft.activeGroupMessageIndex = null;
    } else if (messages[draft.activeGroupMessageIndex]) {
      // Handle the case where there is a message for the current index
      draft.activeMessageId = messages[draft.activeGroupMessageIndex].id;
      // Don't automatically focus the element for the new message
      draft.skipNextElementFocus = true;
    } else {
      // Choose the first message in the group
      draft.activeMessageId = messages[0].id;
      draft.activeGroupMessageIndex = 0;
      // Don't automatically focus the element for the new message
      draft.skipNextElementFocus = true;
    }
  }
}

const useStore = createSelectorHooks(useStoreBase);

export default useStore;
